import {
  applyOperation,
  formatEquation,
  normalizeOperation,
  normalizeRational,
  type AlgebraOperation,
  type Equation,
} from '../../shared/algebra.ts';

export type Demonstration = {
  valid: boolean;
  before: string;
  after: string;
  operationText: string;
  leftCancellation: boolean;
  rightCancellation: boolean;
  wrapsExpressions: boolean;
  left: { variable: string; constant: string };
  right: { variable: string; constant: string };
};

const operationSymbol: Record<AlgebraOperation['kind'], string> = {
  add: '+', subtract: '−', multiply: '×', divide: '÷',
};

function operationLabel(operation: AlgebraOperation) {
  const value = operation.value;
  if (value.startsWith('-') && (operation.kind === 'add' || operation.kind === 'subtract'))
    return `${operation.kind === 'add' ? '−' : '+'} ${value.slice(1)}`;
  return `${operationSymbol[operation.kind]} ${value}`;
}

function isInverseConstant(constant: string, operation: AlgebraOperation) {
  if (operation.value.includes('x')) return false;
  try {
    const value = normalizeRational(operation.value);
    if (operation.kind === 'subtract') return constant === value;
    if (operation.kind === 'add')
      return constant === (value.startsWith('-') ? value.slice(1) : `-${value}`);
  } catch {
    return false;
  }
  return false;
}

function sideTerms(side: Equation['left']) {
  const variable =
    side.x === '0' ? '' : side.x === '1' ? 'x' : side.x === '-1' ? '−x' : `${side.x}x`;
  return { variable, constant: side.constant };
}

/** Text counterpart of the component's split-term rendering, for static QA. */
export function formatOperationExpression(
  terms: { variable: string; constant: string },
  operationText: string,
  wraps: boolean,
) {
  const magnitude = terms.constant.startsWith('-')
    ? terms.constant.slice(1)
    : terms.constant;
  const constant = terms.variable
    ? magnitude
    : terms.constant.startsWith('-') ? `−${magnitude}` : magnitude;
  const expression = terms.constant === '0'
    ? terms.variable || '0'
    : terms.variable
      ? `${terms.variable}${terms.constant.startsWith('-') ? ' − ' : ' + '}${constant}`
      : constant;
  return wraps ? `(${expression}) ${operationText}` : `${expression} ${operationText}`;
}

export function deriveOperationDemonstration(
  before: Equation,
  operation: AlgebraOperation,
  after: Equation,
): Demonstration {
  try {
    const normalized = normalizeOperation(operation);
    const expected = applyOperation(before, normalized);
    const valid = JSON.stringify(expected) === JSON.stringify(after);
    return {
      valid,
      before: formatEquation(before),
      after: formatEquation(after),
      operationText: operationLabel(normalized),
      leftCancellation: valid && isInverseConstant(before.left.constant, normalized),
      rightCancellation: valid && isInverseConstant(before.right.constant, normalized),
      wrapsExpressions: normalized.kind === 'multiply' || normalized.kind === 'divide',
      left: sideTerms(before.left),
      right: sideTerms(before.right),
    };
  } catch {
    return { valid: false, before: formatEquation(before), after: formatEquation(after), operationText: 'invalid operation', leftCancellation: false, rightCancellation: false, wrapsExpressions: false, left: sideTerms(before.left), right: sideTerms(before.right) };
  }
}
